import type { ToastType } from './types';

const toastIcons: Record<ToastType, string> = {
  error: 'error',
  success: 'check_circle',
  info: 'info',
  warning: 'warning',
};

export function showToast(message: string, type: ToastType = 'info', duration = 4500): void {
  const container = document.getElementById('toastContainer');
  if (!container) return;
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.innerHTML = `<span class="material-symbols-outlined">${toastIcons[type]}</span><span></span>`;
  const msgSpan = toast.querySelectorAll('span')[1];
  if (msgSpan) msgSpan.textContent = message;
  container.appendChild(toast);
  setTimeout(() => {
    toast.classList.add('toast-out');
    toast.addEventListener('animationend', () => toast.remove());
  }, duration);
}

export function $<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Elemento #${id} no encontrado en el DOM`);
  return el as T;
}

export function $maybe<T extends HTMLElement = HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function confirmModal(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal active';
    backdrop.style.zIndex = '3000';

    const confirmBtnClass = opts.danger
      ? 'bg-error text-white hover:opacity-90'
      : 'bg-primary text-white hover:opacity-90';

    backdrop.innerHTML = `
      <div class="bg-surface-bright p-8 rounded-2xl max-w-md w-full border border-outline-variant shadow-premium flex flex-col gap-4">
        <h2 class="font-headline-md text-[20px] text-on-surface">${escapeHtml(opts.title)}</h2>
        <p class="text-on-surface-variant font-body-md">${escapeHtml(opts.message)}</p>
        <div class="flex justify-end gap-3 mt-2">
          <button data-action="cancel" class="px-5 py-2 rounded-xl text-on-surface-variant hover:bg-outline-variant/20 font-bold transition-all">${escapeHtml(opts.cancelLabel ?? 'Cancelar')}</button>
          <button data-action="confirm" class="px-5 py-2 rounded-xl ${confirmBtnClass} font-bold shadow-md transition-all">${escapeHtml(opts.confirmLabel ?? 'Confirmar')}</button>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    const close = (result: boolean): void => {
      backdrop.remove();
      resolve(result);
    };

    backdrop.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === backdrop) close(false);
      if (target.dataset.action === 'cancel') close(false);
      if (target.dataset.action === 'confirm') close(true);
    });

    document.addEventListener(
      'keydown',
      function escHandler(e) {
        if (e.key === 'Escape') {
          document.removeEventListener('keydown', escHandler);
          close(false);
        } else if (e.key === 'Enter') {
          document.removeEventListener('keydown', escHandler);
          close(true);
        }
      },
      { once: false }
    );
  });
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function formatTime(seconds: number): string {
  const d = new Date(seconds * 1000);
  return d.toISOString().substring(11, 19).replace(/^00:/, '');
}
